import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useConfirm } from '../components/ConfirmModal';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { entitledPlan } from '../utils/entitlements';
import API from '../utils/api';
import { trackSeriesCreated } from '../utils/analytics';

const GENRES = ['adventure','fantasy','mystery','educational','bedtime','funny','nature','science'];
const TONES  = ['magical','funny','exciting','calm','educational','spooky (mild)','heartwarming'];
const LANGS  = [
  { code: 'en', label: '🇬🇧 English' },    { code: 'es', label: '🇪🇸 Spanish' },
  { code: 'fr', label: '🇫🇷 French' },     { code: 'hi', label: '🇮🇳 Hindi' },
  { code: 'gu', label: '🇮🇳 Gujarati' },   { code: 'de', label: '🇩🇪 German' },
  { code: 'pt', label: '🇧🇷 Portuguese' }, { code: 'zh', label: '🇨🇳 Chinese' },
  { code: 'it', label: '🇮🇹 Italian' },    { code: 'ja', label: '🇯🇵 Japanese' },
  { code: 'ar', label: '🇸🇦 Arabic' },
];

const STATUS_COLOR = { active: '#059669', completed: '#6f42c1', paused: '#d97706' };
const STATUS_BG    = { active: '#f0fdf4', completed: '#f0ebff', paused: '#fffbeb' };

const SeriesPage = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [series, setSeries]     = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating]   = useState(false);
  const [createError, setCreateError] = useState('');

  // New series form
  const [form, setForm] = useState({
    childId: '', title: '', description: '', genre: 'adventure', tone: 'magical',
    language: 'en', characters: '', setting: '',
  });

  const isPaid = entitledPlan(user) !== 'free';

  const loadSeries = useCallback(() => {
    setLoading(true);
    API.get('/series').then(r => setSeries(r.data.series)).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSeries();
    API.get('/children').then(r => {
      setChildren(r.data.children || []);
      if (r.data.children?.length) setForm(f => ({ ...f, childId: r.data.children[0]._id }));
    });
  }, [loadSeries]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.childId) return setCreateError('Please select a child.');
    if (!form.title.trim()) return setCreateError('Series title is required.');
    setCreating(true); setCreateError('');
    try {
      const characters = form.characters.split(',').map(c => c.trim()).filter(Boolean);
      const r = await API.post('/series', { ...form, characters });
      trackSeriesCreated();
      setShowModal(false);
      setForm(f => ({ ...f, title: '', description: '', characters: '', setting: '' }));
      navigate(`/dashboard/series/${r.data.series._id}`);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create series.');
    } finally { setCreating(false); }
  };

  const deleteSeries = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    const ok = await confirm({ title: 'Delete series?', message: 'This series and all its chapters will be permanently deleted.', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    await API.delete(`/series/${id}`);
    setSeries(s => s.filter(x => x._id !== id));
  };

  return (
    <DashboardLayout title="Story Series">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            📚 Story Series
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
            Multi-chapter adventures that keep your child coming back every night.
          </p>
        </div>
        {isPaid ? (
          <button
            className="btn btn-dream rounded-pill px-4 fw-semibold"
            onClick={() => setShowModal(true)}
            style={{ fontSize: '14px' }}>
            <i className="bi bi-plus-lg me-2"></i>Start New Series
          </button>
        ) : (
          <Link to="/pricing" className="btn btn-outline-secondary rounded-pill px-4 fw-semibold" style={{ fontSize: '14px' }}>
            <i className="bi bi-lock me-2"></i>Upgrade to Unlock
          </Link>
        )}
      </div>

      {/* Free plan banner */}
      {!isPaid && (
        <div className="alert border-0 mb-4 p-4" style={{ borderRadius: '16px', background: 'linear-gradient(135deg,#f0e8ff,#e8d5ff)' }}>
          <div className="d-flex align-items-start gap-3">
            <span style={{ fontSize: '32px' }}>📚</span>
            <div>
              <div className="fw-bold mb-1" style={{ color: 'var(--text-main)' }}>Story Series is a paid feature</div>
              <p className="text-muted mb-2" style={{ fontSize: '14px' }}>
                Create unlimited multi-chapter story series on the Basic or Family plan. Each chapter uses one story from your monthly allowance.
              </p>
              <Link to="/pricing" className="btn btn-dream btn-sm rounded-pill px-4">
                View Plans
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Series grid */}
      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
        </div>
      ) : series.length === 0 ? (
        <div className="card border-0 shadow-sm text-center py-5 px-4" style={{ borderRadius: '24px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📖</div>
          <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
            No series yet
          </h5>
          <p className="text-muted mb-4" style={{ fontSize: '15px', maxWidth: '400px', margin: '0 auto 24px' }}>
            Start your child's first multi-chapter adventure. Pick a title, a setting, and recurring characters — then generate one chapter at a time.
          </p>
          {isPaid && (
            <button className="btn btn-dream rounded-pill px-5 fw-semibold mx-auto" onClick={() => setShowModal(true)}>
              <i className="bi bi-plus-lg me-2"></i>Start First Series
            </button>
          )}
        </div>
      ) : (
        <div className="row g-3">
          {series.map(s => (
            <div key={s._id} className="col-sm-6 col-lg-4">
              <Link to={`/dashboard/series/${s._id}`} style={{ textDecoration: 'none' }}>
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '20px', overflow: 'hidden', transition: 'transform 0.15s, box-shadow 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(111,66,193,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>

                  {/* Cover */}
                  <div style={{ height: '140px', background: s.coverIllustrationUrl ? `url(${s.coverIllustrationUrl}) center/cover` : 'linear-gradient(135deg,#2d1b69,#6f42c1)', position: 'relative' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
                    <div style={{ position: 'absolute', bottom: '12px', left: '14px', right: '40px' }}>
                      <div style={{ color: '#fff', fontFamily: "'Fredoka One',cursive", fontSize: '16px', lineHeight: 1.3, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                        {s.title}
                      </div>
                    </div>
                    {/* Status badge */}
                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                      <span className="badge rounded-pill px-2 py-1" style={{ background: STATUS_BG[s.status], color: STATUS_COLOR[s.status], fontSize: '10px' }}>
                        {s.status}
                      </span>
                    </div>
                    {/* Delete btn */}
                    <button
                      onClick={(e) => deleteSeries(e, s._id)}
                      className="btn btn-sm position-absolute"
                      style={{ top: '8px', left: '8px', background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', borderRadius: '6px', padding: '2px 7px', fontSize: '12px', lineHeight: 1.5 }}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>

                  <div className="card-body p-3">
                    {/* Child */}
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span style={{ fontSize: '18px' }}>{s.child?.avatar || '🧒'}</span>
                      <span style={{ fontSize: '13px', color: 'var(--text-muted2)' }}>{s.child?.name}, age {s.child?.age}</span>
                    </div>

                    {/* Description */}
                    {s.description && (
                      <p style={{ fontSize: '13px', color: 'var(--text-muted2)', lineHeight: 1.5, marginBottom: '10px',
                        overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {s.description}
                      </p>
                    )}

                    {/* Footer */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2">
                        <span className="badge rounded-pill" style={{ background: 'var(--purple-section)', color: '#6f42c1', fontSize: '11px' }}>
                          {s.chapterCount} chapter{s.chapterCount !== 1 ? 's' : ''}
                        </span>
                        <span className="badge rounded-pill" style={{ background: 'var(--header-bg)', color: 'var(--text-muted2)', fontSize: '11px' }}>
                          {s.genre}
                        </span>
                      </div>
                      <span className="text-muted" style={{ fontSize: '11px' }}>
                        {s.language?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* ── Create Series Modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content border-0" style={{ borderRadius: '24px' }}>
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <div>
                  <h5 className="fw-bold mb-0" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive" }}>
                    📚 Start a New Story Series
                  </h5>
                  <p className="text-muted small mb-0">Define your world — then generate one chapter at a time.</p>
                </div>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>

              <div className="modal-body px-4 py-3">
                {createError && <div className="alert alert-danger py-2 small mb-3">{createError}</div>}

                <form onSubmit={handleCreate} id="create-series-form">
                  {/* Child + Title */}
                  <div className="row g-3 mb-3">
                    <div className="col-sm-5">
                      <label className="form-label fw-semibold small">For which child? <span className="text-danger">*</span></label>
                      <select className="form-select" style={{ borderRadius: '10px' }}
                        value={form.childId} onChange={e => setForm(f => ({ ...f, childId: e.target.value }))}>
                        {children.map(c => (
                          <option key={c._id} value={c._id}>{c.avatar} {c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-7">
                      <label className="form-label fw-semibold small">Series title <span className="text-danger">*</span></label>
                      <input className="form-control" placeholder='e.g. "The Adventures of Luna"' style={{ borderRadius: '10px' }}
                        value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Premise / description <span className="text-muted fw-normal">(optional)</span></label>
                    <textarea className="form-control" rows={2} style={{ borderRadius: '10px', resize: 'none' }}
                      placeholder='e.g. "Luna is a brave girl who explores a magical forest kingdom with her dragon friend Sparks."'
                      value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={500} />
                  </div>

                  {/* Characters */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Recurring characters <span className="text-muted fw-normal">(comma-separated, optional)</span></label>
                    <input className="form-control" style={{ borderRadius: '10px' }}
                      placeholder='e.g. "Luna the brave explorer, Sparks the friendly dragon, Elder Oak the wise tree"'
                      value={form.characters} onChange={e => setForm(f => ({ ...f, characters: e.target.value }))} />
                    <div className="form-text">These characters will appear in every chapter.</div>
                  </div>

                  {/* Setting */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Setting / world <span className="text-muted fw-normal">(optional)</span></label>
                    <input className="form-control" style={{ borderRadius: '10px' }}
                      placeholder='e.g. "A magical forest kingdom with talking animals and hidden waterfalls"'
                      value={form.setting} onChange={e => setForm(f => ({ ...f, setting: e.target.value }))} />
                  </div>

                  {/* Genre + Tone + Language */}
                  <div className="row g-3">
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold small">Genre</label>
                      <select className="form-select" style={{ borderRadius: '10px' }}
                        value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))}>
                        {GENRES.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold small">Tone</label>
                      <select className="form-select" style={{ borderRadius: '10px' }}
                        value={form.tone} onChange={e => setForm(f => ({ ...f, tone: e.target.value }))}>
                        {TONES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold small">Language</label>
                      <select className="form-select" style={{ borderRadius: '10px' }}
                        value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}>
                        {LANGS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer border-0 px-4 pb-4 pt-2">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" form="create-series-form" disabled={creating}
                  className="btn btn-dream rounded-pill px-5 fw-semibold">
                  {creating
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
                    : <><i className="bi bi-book me-2"></i>Create Series</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SeriesPage;
